import {Card, ColorSchemeType, Flag, FormControl, FormField} from 'platform/components';
import {Box, Heading, Hide, HStack, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {always, isNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import i18n from '@dms/i18n';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {useTireSetOptions} from '../hooks/useTireSetOptions';
import {ExistingWheel} from '../types/ExistingWheel';
import {TireSetDetailFormDefinition} from '../types/TireSetDetailFormDefinition';

type WheelKey = keyof TireSetDetailFormDefinition['wheels']['0'];
type TireSetOptions = ReturnType<typeof useTireSetOptions>;

interface WheelCardProps extends TestIdProps {
  index: number;
  wheelId: string | Nullish;
  control: FormControl<TireSetDetailFormDefinition>;
  existingWheels?: ExistingWheel[];
  locationOptions: Array<{label: string; value: string}>;
  manufacturerOptions: Array<{label: string; value: string}>;
  tireSetOptions: Pick<
    TireSetOptions,
    | 'seasonOptions'
    | 'rimOptions'
    | 'tireWidthOptions'
    | 'aspectRatioOptions'
    | 'loadIndexOptions'
    | 'speedRatioOptions'
    | 'rimTypeOptions'
    | 'tireConditionOptions'
  >;
  onPropagateValue: (key: WheelKey, index: number) => void;
}

const getWheelStatus = (wheelId: string | Nullish, existingWheels?: ExistingWheel[]) => {
  if (isNil(wheelId)) {
    return;
  }

  const state = existingWheels?.find((wheel) => wheel?.id === wheelId)?.state;

  const label = match(state)
    .with('ARCHIVED', always(i18n.t('entity.tireSet.labels.archived')))
    .with('INSTOCK', always(i18n.t('entity.tireSet.labels.inStock')))
    .with('PICKED', always(i18n.t('entity.tireSet.labels.picked')))
    .with('NEW', always(i18n.t('general.labels.new')))
    .otherwise(always(undefined));

  const colorScheme = match<string | Nullish, ColorSchemeType | undefined>(state)
    .with('ARCHIVED', always('neutral'))
    .with('INSTOCK', always('green'))
    .with('PICKED', always('red'))
    .with('NEW', always('blue'))
    .otherwise(always(undefined));

  return {label, colorScheme};
};

export function WheelCard(props: WheelCardProps) {
  const wheelStatus = getWheelStatus(props.wheelId, props.existingWheels);

  return (
    <Box width={82} minWidth={82}>
      <Card variant="inlineGrey">
        <VStack spacing={4}>
          <HStack spacing={3}>
            <FormField
              control={props.control}
              name={`wheels.${props.index}.checked`}
              type="checkbox"
              data-testid={suffixTestId(`${props.index}-selected`, props)}
            />
            <Heading size={4}>
              {`${props.index + 1}. ${i18n.t('entity.tireSet.labels.wheel')}`}
            </Heading>
            <Hide when={isNilOrEmpty(wheelStatus)}>
              <Flag
                label={wheelStatus?.label ?? ''}
                colorScheme={wheelStatus?.colorScheme}
                data-testid={suffixTestId(`${props.index}-wheelStatus`, props)}
              />
            </Hide>
          </HStack>
          <FormField
            control={props.control}
            name={`wheels.${props.index}.manufacturer`}
            type="choice"
            label={i18n.t('entity.tireSet.labels.manufacturer')}
            options={props.manufacturerOptions}
            onChange={() => props.onPropagateValue('manufacturer', props.index)}
            menuInPortal
            data-testid={suffixTestId(`${props.index}-manufacturer`, props)}
          />
          <FormField
            control={props.control}
            name={`wheels.${props.index}.title`}
            type="textarea"
            label={i18n.t('entity.tireSet.labels.title')}
            onBlur={() => props.onPropagateValue('title', props.index)}
            data-testid={suffixTestId(`${props.index}-title`, props)}
          />
          <HStack spacing={3}>
            <Box flex={1}>
              <FormField
                control={props.control}
                name={`wheels.${props.index}.tireWidth`}
                type="choice"
                label={i18n.t('entity.tireSet.labels.tireWidth')}
                options={props.tireSetOptions.tireWidthOptions}
                menuInPortal
                onChange={() => props.onPropagateValue('tireWidth', props.index)}
                data-testid={suffixTestId(`${props.index}-tireWidth`, props)}
              />
            </Box>
            <Box flex={1}>
              <FormField
                control={props.control}
                name={`wheels.${props.index}.aspectRatio`}
                type="choice"
                label={i18n.t('entity.tireSet.labels.aspectRatio')}
                options={props.tireSetOptions.aspectRatioOptions}
                menuInPortal
                onChange={() => props.onPropagateValue('aspectRatio', props.index)}
                data-testid={suffixTestId(`${props.index}-aspectRatio`, props)}
              />
            </Box>
          </HStack>
          <HStack spacing={3}>
            <Box flex={1}>
              <FormField
                control={props.control}
                name={`wheels.${props.index}.rim`}
                type="choice"
                label={i18n.t('entity.tireSet.labels.rim')}
                options={props.tireSetOptions.rimOptions}
                menuInPortal
                onChange={() => props.onPropagateValue('rim', props.index)}
                data-testid={suffixTestId(`${props.index}-rim`, props)}
              />
            </Box>
            <Box flex={1}>
              <FormField
                control={props.control}
                name={`wheels.${props.index}.tread`}
                type="number"
                label={i18n.t('entity.tireSet.labels.tread')}
                suffix="mm"
                onBlur={() => props.onPropagateValue('tread', props.index)}
                data-testid={suffixTestId(`${props.index}-tread`, props)}
              />
            </Box>
          </HStack>
          <HStack spacing={3}>
            <Box flex={1}>
              <FormField
                control={props.control}
                name={`wheels.${props.index}.rimType`}
                type="choice"
                label={i18n.t('entity.tireSet.labels.rimType')}
                options={props.tireSetOptions.rimTypeOptions ?? []}
                onChange={() => props.onPropagateValue('rimType', props.index)}
                menuInPortal
                data-testid={suffixTestId(`${props.index}-rimType`, props)}
              />
            </Box>
            <Box flex={1}>
              <FormField
                control={props.control}
                name={`wheels.${props.index}.season`}
                type="choice"
                label={i18n.t('entity.tireSet.labels.season')}
                options={props.tireSetOptions.seasonOptions ?? []}
                onChange={() => props.onPropagateValue('season', props.index)}
                menuInPortal
                data-testid={suffixTestId(`${props.index}-season`, props)}
              />
            </Box>
          </HStack>
          <HStack spacing={3}>
            <Box flex={1}>
              <FormField
                control={props.control}
                name={`wheels.${props.index}.li`}
                type="choice"
                label={i18n.t('entity.tireSet.labels.li')}
                options={props.tireSetOptions.loadIndexOptions}
                menuInPortal
                onChange={() => props.onPropagateValue('li', props.index)}
                data-testid={suffixTestId(`${props.index}-li`, props)}
              />
            </Box>
            <Box flex={1}>
              <FormField
                control={props.control}
                name={`wheels.${props.index}.sr`}
                type="choice"
                label={i18n.t('entity.tireSet.labels.sr')}
                options={props.tireSetOptions.speedRatioOptions}
                menuInPortal
                onChange={() => props.onPropagateValue('sr', props.index)}
                data-testid={suffixTestId(`${props.index}-sr`, props)}
              />
            </Box>
          </HStack>
          <HStack spacing={3}>
            <Box flex={1}>
              <FormField
                control={props.control}
                name={`wheels.${props.index}.dot`}
                type="text"
                label={i18n.t('entity.tireSet.labels.dot')}
                onBlur={() => props.onPropagateValue('dot', props.index)}
                data-testid={suffixTestId(`${props.index}-dot`, props)}
              />
            </Box>
            <Box flex={1}>
              <FormField
                control={props.control}
                name={`wheels.${props.index}.location`}
                type="choice"
                label={i18n.t('entity.tireSet.labels.location')}
                options={props.locationOptions}
                menuInPortal
                onChange={() => props.onPropagateValue('location', props.index)}
                data-testid={suffixTestId(`${props.index}-location`, props)}
              />
            </Box>
          </HStack>
          <HStack spacing={3}>
            <Box flex={1}>
              <FormField
                control={props.control}
                name={`wheels.${props.index}.hasHubcap`}
                type="checkbox"
                label={i18n.t('entity.tireSet.labels.hubcap')}
                onChange={() => props.onPropagateValue('hasHubcap', props.index)}
                data-testid={suffixTestId(`${props.index}-hasHubcap`, props)}
              />
            </Box>
            <Box flex={1}>
              <FormField
                control={props.control}
                name={`wheels.${props.index}.hasWheelLocks`}
                type="checkbox"
                label={i18n.t('entity.tireSet.labels.wheelLocks')}
                onChange={() => props.onPropagateValue('hasWheelLocks', props.index)}
                data-testid={suffixTestId(`${props.index}-hasWheelLocks`, props)}
              />
            </Box>
          </HStack>
          <FormField
            control={props.control}
            name={`wheels.${props.index}.tireCondition`}
            type="choice"
            options={props.tireSetOptions.tireConditionOptions}
            label={i18n.t('general.labels.tireCondition')}
            onChange={() => props.onPropagateValue('tireCondition', props.index)}
            menuInPortal
            data-testid={suffixTestId(`${props.index}-tireCondition`, props)}
          />

          <FormField
            control={props.control}
            name={`wheels.${props.index}.note`}
            type="textarea"
            label={i18n.t('general.labels.note')}
            data-testid={suffixTestId(`${props.index}-note`, props)}
          />
        </VStack>
      </Card>
    </Box>
  );
}
